import { round2, toNumber } from './money';
import { itemHasBackendImage, itemInitialFromLabel, resolveItemColor } from './itemVisuals';
import { getGroupMinSel, groupLimitsUseTotalSum, optionQtyBounds, pickMultipleDistinctOptions } from './modifierGroupFlags';

function numPrice(v) {
  if (v === null || v === undefined) return 0;
  const s = String(v).replace(/[^0-9.-]/g, '');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function optionKey(o) {
  return String(o?.uuid ?? o?.id ?? '');
}

function groupKey(g) {
  return String(g?.uuid ?? g?.id ?? '');
}

function optionDisplayName(o) {
  return String(o?.name ?? o?.option_name ?? o?.label ?? '').trim() || 'Option';
}

export function labelRestaurantItem(item) {
  const fromTranslation = Array.isArray(item?.translations)
    ? item.translations.find((t) => (t?.locale || '').toLowerCase() === 'en')?.item_name
    : '';
  return String(fromTranslation || item?.item_name || '').trim();
}

/**
 * True when defaults cannot satisfy required modifiers (needs full customize modal).
 */
export function menuItemNeedsCustomizeModal(item) {
  const groups = Array.isArray(item?.modifier_groups) ? item.modifier_groups : [];
  for (const g of groups) {
    const req = Number(g.is_required) === 1;
    const gmin = getGroupMinSel(g, req);
    if (pickMultipleDistinctOptions(g) && gmin > 0) return true;
    if (groupLimitsUseTotalSum(g) && gmin > 0) return true;
  }
  return false;
}

/**
 * Builds a cart lineInput using the same defaults as MenuItemCustomizeModal initial state
 * (first variation, first option per modifier group). Returns null if unsafe to auto-add.
 */
export function buildDefaultLineInputFromMenuItem(item) {
  if (!item || menuItemNeedsCustomizeModal(item)) return null;

  const basePrice = numPrice(item?.price);
  const variations = Array.isArray(item?.variations) ? item.variations : [];
  let variant = null;
  if (variations.length) {
    const v = variations[0];
    const id = optionKey(v);
    if (!id) return null;
    const vp = numPrice(v.price ?? v.sale_price);
    const name = String(v.variation ?? v.name ?? '').trim();
    variant = { id, name: name || 'Option', priceDelta: round2(vp - basePrice) };
  }

  const addons = [];
  const groups = Array.isArray(item?.modifier_groups) ? item.modifier_groups : [];
  for (const g of groups) {
    const gk = groupKey(g);
    if (!gk) continue;
    const opts = Array.isArray(g.options) ? g.options : [];
    const req = Number(g.is_required) === 1;
    const gmin = getGroupMinSel(g, req);
    if (pickMultipleDistinctOptions(g)) {
      if (gmin > 0) return null;
      continue;
    }
    if (!req) continue;
    const first = opts[0];
    if (!first) continue;
    const o = first;
    const { min, max } = optionQtyBounds(o);
    const q = Math.min(max, Math.max(min, 1));
    addons.push({
      id: optionKey(o),
      name: optionDisplayName(o),
      price: round2(numPrice(o.price) * q),
      qty: q,
    });
  }

  return {
    itemId: String(item.uuid || item.id || '').trim(),
    name: labelRestaurantItem(item) || 'Item',
    basePrice,
    qty: 1,
    notes: '',
    variant,
    addons,
    imageUrl: itemHasBackendImage(item) ? String(item.item_photo_url || '').trim() : '',
    imageColor: resolveItemColor(item),
    imageInitial: itemInitialFromLabel(labelRestaurantItem(item) || 'Item'),
  };
}

export function itemKeyForCart(it) {
  return String(it?.uuid ?? it?.id ?? '').trim();
}

function shouldShowOnCart(item) {
  const value = item?.show_on_cart;
  if (value === true || value === 1) return true;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'true' || normalized === 'yes';
  }
  return false;
}

export function sortItemsForAddonRail(items, cartLineItemIds) {
  const inCart = new Set((cartLineItemIds || []).map((x) => String(x)));
  const list = (items || []).filter((it) => {
    const k = itemKeyForCart(it);
    return k && shouldShowOnCart(it) && !inCart.has(k) && buildDefaultLineInputFromMenuItem(it);
  });
  return [...list].sort((a, b) => toNumber(a?.price, 0) - toNumber(b?.price, 0)).slice(0, 16);
}
